import type { ResultOf } from '@horof/shared';
import { graphql } from './gql';

/**
 * Bills and payments on the desk (doc 57): lists paged by arqam, the bill's
 * page, a payment's drawer. Amounts and statuses are arqam's.
 */

export const billingContext = {
  additionalTypenames: [
    'Bill',
    'Payment',
    'WhatsappMessage',
    'Meter',
    'Member',
  ],
};

export const BillsDocument = graphql(`
  query DeskBills(
    $filter: BillFilter
    $orderBy: [BillOrderBy!]
    $first: Int!
    $offset: Int!
  ) {
    bills(filter: $filter, orderBy: $orderBy, first: $first, offset: $offset) {
      totalCount
      nodes {
        id
        billMonth
        billStatus
        billTotal
        paidAmount
        remainingAmount
        consumption
        previousReading
        currentReading
        readingPhotoId
        member {
          id
          fullName
        }
        meter {
          id
          label
        }
      }
    }
  }
`);

export type BillRow = NonNullable<
  NonNullable<ResultOf<typeof BillsDocument>['bills']>['nodes'][number]
>;

/** What the bill's page adds to the shared details. */
export const BillPageDocument = graphql(`
  query DeskBillPage($id: UUID!) {
    bill(id: $id) {
      id
      billMonth
      createdAt
      updatedAt
      member {
        id
        fullName
      }
      meter {
        id
        label
      }
      whatsappMessages(orderBy: [CREATED_AT_DESC]) {
        nodes {
          id
          kind
          template
          status
          toPhone
          queuedBy
          createdAt
          sentAt
          deliveredAt
          readAt
          error
        }
      }
    }
  }
`);

export const PaymentsDocument = graphql(`
  query DeskPayments(
    $filter: PaymentFilter
    $orderBy: [PaymentOrderBy!]
    $first: Int!
    $offset: Int!
  ) {
    payments(
      filter: $filter
      orderBy: $orderBy
      first: $first
      offset: $offset
    ) {
      totalCount
      nodes {
        id
        paymentDate
        receiptNo
        amount
        receivedBy
        bill {
          id
          billMonth
          member {
            id
            fullName
          }
          meter {
            label
          }
        }
      }
    }
  }
`);

export type PaymentRow = NonNullable<
  NonNullable<ResultOf<typeof PaymentsDocument>['payments']>['nodes'][number]
>;

/** Who received payments lately: the «استلمها» filter's choices. */
export const ReceiversDocument = graphql(`
  query DeskPaymentReceivers {
    payments(orderBy: [CREATED_AT_DESC], first: 1000) {
      nodes {
        receivedBy
      }
    }
  }
`);

export const PaymentDocument = graphql(`
  query DeskPayment($id: UUID!) {
    payment(id: $id) {
      id
      paymentDate
      receiptNo
      amount
      receivedBy
      receiptLink
      bill {
        id
        billMonth
        billTotal
        remainingAmount
        billStatus
        member {
          id
          fullName
        }
        meter {
          id
          label
        }
      }
    }
  }
`);
